import clsx from 'clsx';
import {filter, map, negate, size} from 'lodash';
import numeral from 'numeraljs';

import Loading from 'components/Loading';
import InlinePage from 'components/Page/Inline';
import SummaryAnnotationTable from 'components/SummaryAnnotation/Table';
import KyError from 'components/errors/KyError';
import WarningIcon from 'components/icons/Warning';
import Link from 'components/links/Link';
import FullFluidWidthTemplate from 'components/templates/FullFluidWidth';
import useAppContext from 'conf/AppContext';
import usePediatricContext from 'context/Pediatric';
import {useGet} from 'helpers/KyHooks';
import SummaryAnnotationGraph from 'components/SummaryAnnotation/Graph';

export default function SummaryAnnotationPage({level = '1A'}) {
  const appContext = useAppContext();
  const pdContext = usePediatricContext();
  const {response, error} = useGet(`site/clinicalAnnotations/${level}`, {cacheResponse: !appContext});
  const levels = ['1A', '1B', '2A', '2B', '3', '4'];

  if (error) {
    appContext.toastError(<KyError kyError={error} />);
  }
  let body = <Loading />;
  if (response) {
    const TableComp = pdContext.isPediatricMode ? SplitSummaryTables : SingleSummaryTable;
    const {clinicalAnnotations, pgxLevelStats} = response.data;
    const notSupported = size(clinicalAnnotations) === 0;
    body = (
      <>
        <div className="container">
          <div className="d-flex">
            <InlinePage id="summaryAnnotationsHeader" />
            <SummaryAnnotationGraph className="float-right" />
          </div>
          <p className="text-center font-weight-bold mt-4">Pick a level to see annotations</p>
          <ul className="nav nav-pills justify-content-center mt-1 mb-3">
            {map(levels, (l) => <LevelNavItem key={l} label={l} active={l === level} n={pgxLevelStats[l]} />)}
          </ul>
        </div>
        {!notSupported && <TableComp anns={clinicalAnnotations} level={level} />}
        {notSupported && (
          <div className="container">
            <div className="alert alert-warning">
              <WarningIcon className="mr-1" />
              Level {level} has too many annotations ({numeral(pgxLevelStats[level]).format('0,0')}) to list here. You can see these on the individual gene, drug, variant, and haplotype pages or get them on the <Link href="/downloads">Downloads page</Link>.
            </div>
          </div>
        )}
      </>
    );
  }
  
  return (
    <FullFluidWidthTemplate title="Summary Annotations">
      {body}
    </FullFluidWidthTemplate>
  );
}

function LevelNavItem({label, active, n}) {
  const appContext = useAppContext();
  const baseUrl = appContext.getCurrentPath().split('?')[0];
  let nDisplay;
  if (n) {
    nDisplay = <small className="ml-1">(n={n})</small>;
  }
  return (
    <li className="nav-item">
      <a className={clsx('nav-link', {active})} href={`${baseUrl}?level=${label}`}>
        <span className="font-weight-bold">{label}</span>{nDisplay}
      </a>
    </li>
  );
}

function SingleSummaryTable({anns = [], level}) {
  return (
    <SummaryAnnotationTable
      data={anns}
      canEditColumns={true}
      canHideFilters={true}
      canShowFullscreen={true}
      disableExport={false}
      singularNoun={`Level ${level} summary annotation`}
    />
  );
}

const pedsPredicate = (a) => a.tags.includes('pediatric') || a.tags.includes('pediatric-only');

function SplitSummaryTables({anns = [], level}) {
  const pedsAnns = filter(anns, pedsPredicate);
  const otherAnns = filter(anns, negate(pedsPredicate));
  return (
    <div>
      <div>
        <h3>Pediatric Summary Annotations</h3>
        <SummaryAnnotationTable
          data={pedsAnns}
          canEditColumns={true}
          canHideFilters={true}
          canShowFullscreen={true}
          disableExport={false}
          singularNoun={`Pediatric level ${level} annotation`}
        />
      </div>
      <div>
        <h3>Other Summary Annotations</h3>
        <SummaryAnnotationTable
          data={otherAnns}
          canEditColumns={true}
          canHideFilters={true}
          canShowFullscreen={true}
          disableExport={false}
          singularNoun={`Other level ${level} annotation`}
        />
      </div>
    </div>
  );
}
